/**
 * Sentry integration — client-side error monitoring.
 *
 * Activates only when VITE_SENTRY_DSN is set at build time. Without a DSN this
 * is a no-op (logs to console). Wire the DSN via .env.local + Cloudflare Pages
 * env vars, then rebuild.
 *
 * To get a DSN:
 *   1. Sign up at https://sentry.io (free tier = 5k errors/mo)
 *   2. Create a project → JavaScript / React
 *   3. Copy the DSN URL (https://<key>@o<org>.ingest.sentry.io/<project>)
 *   4. Add to web/.env.local: VITE_SENTRY_DSN=https://...
 *   5. Rebuild + redeploy
 *
 * Setup is intentionally lazy — no import cost when unconfigured.
 */

import type { init as SentryInit, captureException as SentryCapture, captureMessage as SentryMessage } from '@sentry/react'

let inited = false
let sentryCaptureException: typeof SentryCapture | null = null
let sentryCaptureMessage: typeof SentryMessage | null = null

export async function initErrorMonitor(): Promise<void> {
  if (inited) return
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined
  if (!dsn) {
    // No DSN configured — leave the no-op wrappers in place
    inited = true
    return
  }

  try {
    const Sentry = await import('@sentry/react')
    const initFn = Sentry.init as typeof SentryInit
    initFn({
      dsn,
      environment: (import.meta.env.MODE ?? 'production'),
      release: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? 'unversioned',
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0,      // don't record sessions by default (privacy)
      replaysOnErrorSampleRate: 0.5,    // half of errors get a session replay
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
      ],
      beforeSend(event) {
        // Strip any event.request.headers that might contain auth
        if (event.request?.headers) {
          delete event.request.headers.Authorization
          delete event.request.headers.apikey
        }
        return event
      },
    })
    sentryCaptureException = Sentry.captureException
    sentryCaptureMessage = Sentry.captureMessage
    inited = true
    // eslint-disable-next-line no-console
    console.info('[BabyOS] Sentry initialized')
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[BabyOS] Sentry init failed', e)
    inited = true
  }
}

/** Report an error. Non-blocking; safe to call before init. */
export function reportError(err: unknown, context?: Record<string, unknown>): void {
  if (sentryCaptureException) {
    sentryCaptureException(err, context ? { extra: context } : undefined)
  } else {
    // eslint-disable-next-line no-console
    console.error('[BabyOS] error', err, context)
  }
}

/** Report a non-error message (info / warning). Non-blocking. */
export function reportMessage(msg: string, level: 'info' | 'warning' | 'error' = 'info'): void {
  if (sentryCaptureMessage) {
    sentryCaptureMessage(msg, level)
  } else {
    // eslint-disable-next-line no-console
    console.log(`[BabyOS ${level}]`, msg)
  }
}

/** Set the current user for Sentry attribution. Call after sign-in. */
export async function setErrorUser(user: { id: string; email?: string | null } | null): Promise<void> {
  if (!sentryCaptureException) return
  const Sentry = await import('@sentry/react')
  if (user) Sentry.setUser({ id: user.id, email: user.email ?? undefined })
  else Sentry.setUser(null)
}
